import React, { useEffect, useMemo, useState } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import AppDialog from "../components/AppDialog";
import { formatApiError } from "../lib/formatApiError";
import { listOrganizations, listSeasons } from "../graphql/queries";
import { buildAdminScope } from "../lib/adminScope";

interface Program {
  id: string;
  name: string;
  type: string;
  description?: string | null;
  organizationId: string;
  seasonId?: string | null;
}

interface Organization {
  id: string;
  name: string;
}

interface Season {
  id: string;
  name: string;
  year: number;
}

interface ProgramsPageProps {
  userRoles: string[];
}

type SortKey = "name" | "type" | "organization" | "season";

const listProgramsQuery = /* GraphQL */ `
  query ListProgramsForPage($limit: Int, $nextToken: String) {
    listPrograms(limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        type
        description
        organizationId
        seasonId
      }
      nextToken
    }
  }
`;

const createProgramMutation = /* GraphQL */ `
  mutation CreateProgramForPage($input: CreateProgramInput!) {
    createProgram(input: $input) {
      id
      name
      type
      description
      organizationId
      seasonId
    }
  }
`;

const updateProgramMutation = /* GraphQL */ `
  mutation UpdateProgramForPage($input: UpdateProgramInput!) {
    updateProgram(input: $input) {
      id
      name
      type
      description
      organizationId
      seasonId
    }
  }
`;

const deleteProgramMutation = /* GraphQL */ `
  mutation DeleteProgramForPage($input: DeleteProgramInput!) {
    deleteProgram(input: $input) {
      id
    }
  }
`;

const ProgramsPage: React.FC<ProgramsPageProps> = ({ userRoles }) => {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [username, setUsername] = useState("");

  const [filterText, setFilterText] = useState("");
  const [filterOrganizationId, setFilterOrganizationId] = useState("");
  const [filterSeasonId, setFilterSeasonId] = useState("");
  const [filterType, setFilterType] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("TRAVEL");
  const [newDescription, setNewDescription] = useState("");
  const [newOrganizationId, setNewOrganizationId] = useState("");
  const [newSeasonId, setNewSeasonId] = useState("");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingType, setEditingType] = useState("TRAVEL");
  const [editingDescription, setEditingDescription] = useState("");
  const [editingOrganizationId, setEditingOrganizationId] = useState("");
  const [editingSeasonId, setEditingSeasonId] = useState("");

  const adminScope = useMemo(
    () => buildAdminScope({ userRoles, username, organizations, programs }),
    [organizations, programs, userRoles, username]
  );
  const canManage = adminScope.isGlobalAdmin || adminScope.isScopedCommish;

  const loadData = async (silent = false) => {
    try {
      if (!silent) {
        setErrorMessage("");
      }
      const authUser = await Auth.currentAuthenticatedUser();
      setUsername(authUser?.username ?? "");
      const [programRes, orgRes, seasonRes] = await Promise.all([
        API.graphql(graphqlOperation(listProgramsQuery, { limit: 400 })),
        API.graphql(graphqlOperation(listOrganizations, { limit: 300 })),
        API.graphql(graphqlOperation(listSeasons, { limit: 300 }))
      ]);

      setPrograms(((programRes as any)?.data?.listPrograms?.items ?? []).filter(Boolean));
      setOrganizations(((orgRes as any)?.data?.listOrganizations?.items ?? []).filter(Boolean));
      setSeasons(((seasonRes as any)?.data?.listSeasons?.items ?? []).filter(Boolean));
    } catch (error) {
      if (!silent) {
        setErrorMessage(`Failed to load programs: ${formatApiError(error)}`);
      }
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newOrganizationId || !newType.trim()) {
      setErrorMessage("Program name, type, and organization are required.");
      return;
    }
    if (!adminScope.canCreateProgramInOrganization(newOrganizationId)) {
      setErrorMessage("Commish can only create programs in assigned organizations.");
      return;
    }
    try {
      setErrorMessage("");
      await API.graphql(
        graphqlOperation(createProgramMutation, {
          input: {
            name: newName.trim(),
            type: newType.trim(),
            description: newDescription.trim() || null,
            organizationId: newOrganizationId,
            seasonId: newSeasonId || null
          }
        })
      );
      setNewName("");
      setNewType("TRAVEL");
      setNewDescription("");
      setNewOrganizationId("");
      setNewSeasonId("");
      setCreateOpen(false);
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to create program: ${formatApiError(error)}`);
    }
  };

  const handleUpdate = async () => {
    if (!editingId || !editingName.trim() || !editingOrganizationId || !editingType.trim()) {
      setErrorMessage("Program name, type, and organization are required.");
      return;
    }
    if (!adminScope.canManageProgram(editingOrganizationId)) {
      setErrorMessage("Commish can only edit assigned programs.");
      return;
    }
    try {
      setErrorMessage("");
      await API.graphql(
        graphqlOperation(updateProgramMutation, {
          input: {
            id: editingId,
            name: editingName.trim(),
            type: editingType.trim(),
            description: editingDescription.trim() || null,
            organizationId: editingOrganizationId,
            seasonId: editingSeasonId || null
          }
        })
      );
      setEditingId(null);
      setEditOpen(false);
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to update program: ${formatApiError(error)}`);
    }
  };

  const handleDelete = async (program: Program) => {
    if (!window.confirm(`Delete program "${program.name}"?`)) {
      return;
    }
    if (!adminScope.canManageProgram(program.organizationId)) {
      setErrorMessage("Commish can only delete assigned programs.");
      return;
    }
    try {
      setErrorMessage("");
      await API.graphql(graphqlOperation(deleteProgramMutation, { input: { id: program.id } }));
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to delete program: ${formatApiError(error)}`);
    }
  };

  const visibleOrganizations = useMemo(
    () => organizations.filter((organization) => adminScope.isGlobalAdmin || adminScope.canManageOrganization(organization.id)),
    [adminScope, organizations]
  );
  const visiblePrograms = useMemo(
    () => programs.filter((program) => adminScope.isGlobalAdmin || adminScope.canManageProgram(program.organizationId)),
    [adminScope, programs]
  );

  const orgNameById = useMemo(
    () => organizations.reduce<Record<string, string>>((acc, org) => ({ ...acc, [org.id]: org.name }), {}),
    [organizations]
  );
  const seasonLabelById = useMemo(
    () =>
      seasons.reduce<Record<string, string>>((acc, season) => {
        acc[season.id] = `${season.name} ${season.year}`;
        return acc;
      }, {}),
    [seasons]
  );

  const rows = useMemo(() => {
    const normalizedText = filterText.trim().toLowerCase();
    const filtered = visiblePrograms.filter((program) => {
      const organizationName = orgNameById[program.organizationId] ?? "";
      const seasonLabel = seasonLabelById[program.seasonId ?? ""] ?? "";
      const haystack = `${program.name} ${program.type} ${program.description ?? ""} ${organizationName} ${seasonLabel}`.toLowerCase();
      if (normalizedText && !haystack.includes(normalizedText)) {
        return false;
      }
      if (filterOrganizationId && program.organizationId !== filterOrganizationId) {
        return false;
      }
      if (filterSeasonId && program.seasonId !== filterSeasonId) {
        return false;
      }
      if (filterType && program.type !== filterType) {
        return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      let compare = 0;
      if (sortKey === "organization") {
        compare = (orgNameById[a.organizationId] ?? "").localeCompare(orgNameById[b.organizationId] ?? "");
      } else if (sortKey === "season") {
        compare = (seasonLabelById[a.seasonId ?? ""] ?? "").localeCompare(seasonLabelById[b.seasonId ?? ""] ?? "");
      } else if (sortKey === "type") {
        compare = a.type.localeCompare(b.type);
      } else {
        compare = a.name.localeCompare(b.name);
      }
      return sortDirection === "asc" ? compare : -compare;
    });
  }, [filterOrganizationId, filterSeasonId, filterText, filterType, orgNameById, seasonLabelById, sortDirection, sortKey, visiblePrograms]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Programs</h1>
      {errorMessage && <p className="mb-4" style={{ color: "#c1121f" }}>{errorMessage}</p>}

      {(adminScope.isGlobalAdmin || adminScope.managedOrganizationIds.size > 0) && (
        <div className="bubble-card p-4 mb-4 flex gap-2">
          <button onClick={() => setCreateOpen(true)} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            Add Program
          </button>
        </div>
      )}

      <div className="bubble-card p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-2">
        <input
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          className="border px-3 py-2 rounded"
          placeholder="Filter by program, type, org, season"
        />
        <select value={filterOrganizationId} onChange={(event) => setFilterOrganizationId(event.target.value)} className="border px-3 py-2 rounded">
          <option value="">All organizations</option>
          {visibleOrganizations.map((organization) => (
            <option key={organization.id} value={organization.id}>{organization.name}</option>
          ))}
        </select>
        <select value={filterSeasonId} onChange={(event) => setFilterSeasonId(event.target.value)} className="border px-3 py-2 rounded">
          <option value="">All seasons</option>
          {seasons.map((season) => (
            <option key={season.id} value={season.id}>{season.name} {season.year}</option>
          ))}
        </select>
        <select value={filterType} onChange={(event) => setFilterType(event.target.value)} className="border px-3 py-2 rounded">
          <option value="">All types</option>
          <option value="TRAVEL">TRAVEL</option>
          <option value="REC">REC</option>
          <option value="CLUB">CLUB</option>
          <option value="OTHER">OTHER</option>
        </select>
      </div>

      <div className="bubble-table-wrapper">
        <table className="bubble-table">
          <thead>
            <tr>
              <th><button className="table-sort" onClick={() => toggleSort("name")}>Program</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("type")}>Type</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("organization")}>Organization</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("season")}>Season</button></th>
              <th>Description</th>
              {canManage && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={canManage ? 6 : 5} className="text-gray-600">No programs found.</td>
              </tr>
            )}
            {rows.map((program) => (
              <tr key={program.id}>
                <td>{program.name}</td>
                <td>{program.type}</td>
                <td>{orgNameById[program.organizationId] ?? "Unknown org"}</td>
                <td>{program.seasonId ? (seasonLabelById[program.seasonId] ?? "Unknown season") : "None"}</td>
                <td>{program.description?.trim() || "-"}</td>
                {canManage && adminScope.canManageProgram(program.organizationId) && (
                  <td>
                    <div className="flex gap-2">
                      <button
                        className="bg-yellow-500 text-white px-3 py-1 rounded"
                        onClick={() => {
                          setEditingId(program.id);
                          setEditingName(program.name);
                          setEditingType(program.type);
                          setEditingDescription(program.description ?? "");
                          setEditingOrganizationId(program.organizationId);
                          setEditingSeasonId(program.seasonId ?? "");
                          setEditOpen(true);
                        }}
                      >
                        Edit
                      </button>
                      <button className="bg-red-600 text-white px-3 py-1 rounded" onClick={() => handleDelete(program)}>
                        Delete
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AppDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add Program"
        footer={
          <>
            <button onClick={() => setCreateOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button onClick={handleCreate} className="bg-blue-600 text-white px-3 py-2 rounded">Save</button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input value={newName} onChange={(event) => setNewName(event.target.value)} className="border px-3 py-2 rounded" placeholder="Program name" />
          <select value={newType} onChange={(event) => setNewType(event.target.value)} className="border px-3 py-2 rounded">
            <option value="TRAVEL">TRAVEL</option>
            <option value="REC">REC</option>
            <option value="CLUB">CLUB</option>
            <option value="OTHER">OTHER</option>
          </select>
          <select value={newOrganizationId} onChange={(event) => setNewOrganizationId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">Select organization</option>
            {visibleOrganizations.map((organization) => (
              <option key={organization.id} value={organization.id}>{organization.name}</option>
            ))}
          </select>
          <select value={newSeasonId} onChange={(event) => setNewSeasonId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No season</option>
            {seasons.map((season) => (
              <option key={season.id} value={season.id}>{season.name} {season.year}</option>
            ))}
          </select>
          <input value={newDescription} onChange={(event) => setNewDescription(event.target.value)} className="border px-3 py-2 rounded md:col-span-2" placeholder="Description" />
        </div>
      </AppDialog>

      <AppDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Program"
        footer={
          <>
            <button onClick={() => setEditOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button onClick={handleUpdate} className="bg-blue-600 text-white px-3 py-2 rounded">Save</button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input value={editingName} onChange={(event) => setEditingName(event.target.value)} className="border px-3 py-2 rounded" placeholder="Program name" />
          <select value={editingType} onChange={(event) => setEditingType(event.target.value)} className="border px-3 py-2 rounded">
            <option value="TRAVEL">TRAVEL</option>
            <option value="REC">REC</option>
            <option value="CLUB">CLUB</option>
            <option value="OTHER">OTHER</option>
          </select>
          <select value={editingOrganizationId} onChange={(event) => setEditingOrganizationId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">Select organization</option>
            {visibleOrganizations.map((organization) => (
              <option key={organization.id} value={organization.id}>{organization.name}</option>
            ))}
          </select>
          <select value={editingSeasonId} onChange={(event) => setEditingSeasonId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No season</option>
            {seasons.map((season) => (
              <option key={season.id} value={season.id}>{season.name} {season.year}</option>
            ))}
          </select>
          <input value={editingDescription} onChange={(event) => setEditingDescription(event.target.value)} className="border px-3 py-2 rounded md:col-span-2" placeholder="Description" />
        </div>
      </AppDialog>
    </div>
  );
};

export default ProgramsPage;
